import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Form,
  Input,
  Space,
  message,
  Dropdown,
  Menu,
  Switch,
  ConfigProvider,
  Empty,
} from "antd";
import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import EmptyData from "../Assets/EmptyData.jpg";

const { Title, Text } = Typography;

const MSPC_BasicDetails = () => {
  const [data, setData] = useState([
    {
      key: 1,
      sno: "01",
      district: "Belgaum",
      mspcname: "Athani Taluk mspc",
      mspccper: "Mallesh Kumar",
      pmcper: "SHIVANNA 7890990808",
      pccper: "SHIVANNA 7890990808",
      gstno: "13121234",
      status: "Active",
      email: "athanimspc@mail.com",
      phone: "8050872345",
    },
    {
      key: 2,
      sno: "02",
      district: "Belgaum",
      mspcname: "Athani Taluk mspc",
      mspccper: "Mallesh Kumar",
      pmcper: "SHIVANNA 7890990808",
      pccper: "SHIVANNA 7890990808",
      gstno: "13121234",
      status: "Inactive",
      email: "athanimspc@mail.com",
      phone: "8050872345",
    },
    // ... rest of your initial data
  ]);

  const [form] = Form.useForm();
  const [editRecord, setEditRecord] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  const originalValuesRef = useRef(null);

  // Card items inside component after data is defined
  const cardItems = [
    {
      title: "Overall MSPC",
      value: data.length,
    },
    {
      title: "Total PM",
      value: data.filter((item) => item.pmcper).length,
    },
  ];

  const filteredData =
    filterStatus === "all"
      ? data
      : data.filter(
          (item) => item.status.toLowerCase() === filterStatus.toLowerCase()
        );

  useEffect(() => {
    if (!isFormOpen) return;

    if (editRecord) {
      const converted = {
        ...editRecord,
        status: editRecord.status.toLowerCase() === "active", // true/false for Switch
      };
      form.setFieldsValue(converted);
      originalValuesRef.current = converted;
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true });
      originalValuesRef.current = null;
    }
  }, [isFormOpen, editRecord]);

  const openNewForm = () => {
    setEditRecord(null);
    setIsFormOpen(true);
  };

  const handleEdit = (record) => {
    setEditRecord(record);
    setIsFormOpen(true);
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    setEditRecord(null);
    originalValuesRef.current = null;
    form.resetFields();
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const statusStr = values.status ? "Active" : "Inactive";

      if (editRecord) {
        setData((prev) =>
          prev.map((item) =>
            item.key === editRecord.key ? { ...item, ...values, status: statusStr } : item
          )
        );
        message.success("MSPC Updated Successfully");
      } else {
        const newRecord = {
          key: Date.now(),
          sno: String(data.length + 1).padStart(2, "0"),
          ...values,
          status: statusStr,
        };
        setData((prev) => [...prev, newRecord]);
        message.success("MSPC Added Successfully");
      }
      handleCancel();
    });
  };

  const handleResetForm = () => {
    if (editRecord) {
      const converted = {
        ...editRecord,
        status: editRecord.status.toLowerCase() === "active",
      };
      form.setFieldsValue(converted);
      message.info("Form restored to original values");
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true });
      message.info("Form reset to blank/default");
    }
  };

  const handleDelete = (key) => {
    setData((prev) => prev.filter((item) => item.key !== key));
    message.success("MSPC Deleted Successfully");
  };

  const exportToCSV = (data, filename = "MSPC_BASICDETAILS_Data") => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(",")];
    data.forEach((row) => {
      const values = headers.map((h) => JSON.stringify(row[h] ?? ""));
      csvRows.push(values.join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = () => exportToCSV(filteredData, "MSPC_BASICDETAILS_Data");

  const columns = [
    { title: "S.NO", dataIndex: "sno", key: "sno" },
    { title: "District", dataIndex: "district", key: "district" },
    { title: "MSPC NAME", dataIndex: "mspcname", key: "mspcname" },
    { title: "MSPC C.Per", dataIndex: "mspccper", key: "mspccper", align: "center" },
    { title: "PM C.Per", dataIndex: "pmcper", key: "pmcper", align: "center" },
    { title: "PC C.Per", dataIndex: "pccper", key: "pccper", align: "center" },
    { title: "GST No.", dataIndex: "gstno", key: "gstno" },
    {
      title: "Active/Inactive",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record.key)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const filterMenu = (
    <Menu
      onClick={(e) => setFilterStatus(e.key)}
      items={[
        { label: "All", key: "all" },
        { label: "Active", key: "active" },
        { label: "Inactive", key: "inactive" },
      ]}
    />
  );

  if (isFormOpen) {
    return (
      <div style={{ padding: 24 }}>
        <h2>{editRecord ? "Edit MSPC" : "New MSPC ONBOARDING"}</h2>

        <Form form={form} layout="vertical">
          <Row gutter={18} justify="space-between" align="middle">
            <Col flex="auto"></Col>
            <Col>
              <Form.Item
                name="status"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={18}>
            <Col span={8}>
              <Form.Item
                name="mspcname"
                label="MSPC NAME"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="district" label="DIST." rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="pmcper" label="PM Incharge" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="pccper" label="PC Incharge" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mspccper" label="MSPC Contact Person">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="Email ID">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="Ph.No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="gstno" label="GST No.">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="panno" label="PAN No.">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tanno" label="TAN No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
  <Form.Item
    name="fssai"
    label={
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 400, fontSize: 14 }}>FSSAI</span>
        <Button
          type="link"
          style={{
            padding: 0,
            fontWeight: 500,
            fontSize: 14,
            marginLeft:"20",
            color: "#1890ff",
            height: "auto",
            lineHeight: "1.5",
            
          }}
        >
          Add New +
        </Button>
      </div>
    }
    style={{ marginBottom: 5 }}
    rules={[{ required: false }]}
  >
    <Input placeholder="Enter FSSAI" />
  </Form.Item>
</Col>

<Col span={4}>
  <Form.Item name="lat" label="Lat" style={{ marginBottom: 5 }}>
    <Input />
  </Form.Item>
</Col>
<Col span={4}>
  <Form.Item name="long" label="Long" style={{ marginBottom: 5 }}>
    <Input />
  </Form.Item>
</Col>

            <Col span={16}>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="pincode" label="Pin Code">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Row style={{display:"flex",flexDirection:"row",justifyContent:"center",alignItems:'center',gap:15}}>
            <Button htmlType="button" onClick={handleResetForm}>
              Reset Form
            </Button>
            <Button onClick={handleSubmit} type="primary">
              {editRecord ? "Update MSPC" : "Save MSPC"}
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </Row>
        </Form>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row
  gutter={16}
  style={{ marginBottom: 20, padding: 20, justifyContent: "space-between", alignItems: "center" }}
>
  {/* Left side - cards */}
  <Col style={{ display: "flex", gap: 16 }}>
    {cardItems.map((item, index) => (
      <Card
        key={index}
        bordered
        style={{ textAlign: "center", borderRadius: 12, minWidth: 180 }}
      >
        <Title level={3} style={{ marginTop: 10 }}>
          {item.title}
        </Title>
        <Text style={{ color: "#0166B1", fontWeight: 600, fontSize: "24px" }}>
          {item.value}
        </Text>
      </Card>
    ))}
  </Col>

  {/* Right side - buttons */}
  <Col style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
    <Button style={{ backgroundColor: "#884af1", color: "#fff" }} onClick={handleExport}>
      Export
    </Button>
    <Button
      icon={<PlusOutlined />}
      style={{ backgroundColor: "#05622C", color: "#fff" }}
      onClick={openNewForm}
    >
      Add New MSPC
    </Button>
    <Dropdown overlay={filterMenu} placement="bottomRight">
      <Button
        icon={<FilterOutlined />}
        type="default"
        style={{ border: "1px solid #bfc5d2", color: "#3096E2" }}
      >
        Filters
      </Button>
    </Dropdown>
  </Col>
</Row>
<ConfigProvider
        renderEmpty={() => (
          <Empty image={EmptyData} description="No MSPC-BASICDETAILS Data Available" imageStyle={{ height: 200 }} />
        )}
      >
        <div style={{ width: "100%", overflowX: "auto", background: "#fff", borderRadius: 8, marginBottom: 20 }}>
          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: "max-content" }}
          />
        </div>
      </ConfigProvider>
    </div>
  );
};

export default MSPC_BasicDetails;
